<template>
  <div class="ph-success">
    <div class="ph-success-card" role="status" aria-live="polite">
      <div v-if="stage === 'verifying'" class="ph-success-stage">
        <div class="ph-success-spinner" aria-hidden="true" />
        <h1>Confirming your payment…</h1>
        <p>Checking with Stripe — this usually takes a second.</p>
      </div>

      <div v-else-if="stage === 'ok'" class="ph-success-stage">
        <div class="ph-success-check" aria-hidden="true">✓</div>
        <h1>You're all set.</h1>
        <p>Receipt on its way to <strong>{{ email }}</strong>. Taking you to your dashboard…</p>
        <p class="ph-success-meta">Amount charged: <strong>${{ formattedAmount }}</strong></p>
      </div>

      <div v-else-if="stage === 'pending'" class="ph-success-stage">
        <div class="ph-success-spinner" aria-hidden="true" />
        <h1>Payment received — wrapping up…</h1>
        <p>Stripe confirmed the charge. Your account is being set up in the background (up to 30 seconds). We'll route you automatically.</p>
        <button type="button" class="ph-success-btn" @click="retry">Check again now</button>
      </div>

      <div v-else class="ph-success-stage ph-success-stage--error" role="alert">
        <div class="ph-success-x" aria-hidden="true">!</div>
        <h1>Couldn't verify the payment</h1>
        <p>{{ errorMessage }}</p>
        <div class="ph-success-actions">
          <button type="button" class="ph-success-btn" @click="retry">Try again</button>
          <router-link class="ph-success-btn ph-success-btn--ghost" :to="{ path: '/pricing' }">Back to pricing</router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

const API_BASE = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000/api';

export default {
  name: 'CheckoutSuccess',
  data() {
    return {
      stage: 'verifying', // verifying | ok | pending | error
      email: '',
      amountCents: 0,
      errorMessage: '',
      retries: 0,
      maxRetries: 6,
    };
  },
  computed: {
    formattedAmount() {
      const v = Number(this.amountCents || 0) / 100;
      return v.toFixed(2);
    },
    reference() {
      // Only pi_ references are accepted now — Checkout Session (cs_) was
      // retired when we switched to embedded Elements. The `pi` alias is
      // kept because it shows up in some old shared links.
      const q = this.$route.query || {};
      return q.payment_intent || q.pi || '';
    },
  },
  async mounted() {
    if (!this.reference) {
      this.stage = 'error';
      this.errorMessage = 'Missing payment reference in URL.';
      return;
    }
    await this.verify();
  },
  methods: {
    async verify() {
      this.stage = 'verifying';
      try {
        const { data, status } = await axios.post(
          `${API_BASE}/new-checkout/checkout/complete`,
          { reference: this.reference },
          { validateStatus: () => true },
        );
        if (status === 200 && data && data.status === 'ok' && data.token) {
          this.storeAuth(data);
          this.stage = 'ok';
          this.email = data.email || '';
          this.amountCents = data.amount_paid_cents || 0;
          // Hard reload (not router.push, not hash-only) so the whole Vue
          // app re-boots with the fresh token. See PricingPage.vue notes.
          setTimeout(() => {
            window.location.assign('/?_=' + Date.now() + '#/dashboard');
          }, 1400);
          return;
        }
        if (status === 202 || (data && data.status === 'pending')) {
          this.stage = 'pending';
          this.retries += 1;
          if (this.retries <= this.maxRetries) {
            setTimeout(() => this.verify(), 3000);
          }
          return;
        }
        this.stage = 'error';
        this.errorMessage = (data && data.message) || 'Payment verification failed.';
      } catch (e) {
        this.stage = 'error';
        this.errorMessage = e.response?.data?.message || 'Network error — please try again.';
      }
    },
    retry() {
      this.retries = 0;
      this.verify();
    },
    storeAuth(data) {
      // See PricingPage.vue#storeAuth for the full list. Must include
      // hot-admin-id + hot-user-id (DashboardLayout trackUserActivity) and
      // hot-sidebar + hot-user-name (Dashboard.vue created() branches on
      // these for the employee path). Any missing key → blank dashboard
      // or session-wipe cascade on first XHR.
      try {
        localStorage.setItem('hot-token', data.token);
        localStorage.setItem('hot-user', data.role || '');
        localStorage.setItem('hot-sidebar', data.sidebar || '');
        localStorage.setItem('hot-email', data.email || '');
        localStorage.setItem('hot-logged-user', String(data.user_id || ''));
        localStorage.setItem('hot-user-full-name', data.full_name || '');
        localStorage.setItem('hot-user-name', data.user_name || data.full_name || '');
        localStorage.setItem('hot-user-id', String(data.user_id || ''));
        if (data.company_id) {
          localStorage.setItem('hot-admin-id', String(data.company_id));
          localStorage.setItem('hot-company-id', String(data.company_id));
        }
        if (axios?.defaults?.headers?.common) {
          axios.defaults.headers.common.Authorization = `Bearer ${data.token}`;
        }
      } catch (e) { /* private mode — dashboard will bounce to login */ }
    },
  },
};
</script>

<style scoped>
.ph-success {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(1200px 600px at 50% -10%, #1a1a1a, #0f0f0f);
  padding: 40px 20px;
  font-family: Inter, system-ui, -apple-system, Segoe UI, sans-serif;
  color: #f4f4f4;
}
.ph-success-card {
  width: 100%;
  max-width: 520px;
  background: #fff;
  color: #1a1a1a;
  border-radius: 18px;
  padding: 40px 36px;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.35);
}
.ph-success-stage {
  text-align: center;
}
.ph-success-stage h1 {
  font-size: 24px;
  font-weight: 700;
  margin: 18px 0 10px;
  color: #1a1a1a;
}
.ph-success-stage p {
  color: #555;
  font-size: 15px;
  line-height: 1.55;
  margin: 0 0 14px;
}
.ph-success-meta {
  font-size: 13px;
  color: #888;
  margin-top: 20px;
}
.ph-success-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background: #fe672c;
  color: #fff;
  font-size: 36px;
  font-weight: 700;
  box-shadow: 0 8px 24px rgba(254, 103, 44, 0.4);
}
.ph-success-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background: #e74c3c;
  color: #fff;
  font-size: 36px;
  font-weight: 700;
}
.ph-success-spinner {
  width: 56px;
  height: 56px;
  border: 5px solid #eee;
  border-top-color: #fe672c;
  border-radius: 50%;
  margin: 0 auto;
  animation: ph-spin 0.8s linear infinite;
}
@keyframes ph-spin {
  to { transform: rotate(360deg); }
}
.ph-success-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 18px;
  flex-wrap: wrap;
}
.ph-success-btn {
  appearance: none;
  border: 0;
  background: #1a1a1a;
  color: #fff;
  padding: 12px 22px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.ph-success-btn--ghost {
  background: transparent;
  color: #fe672c;
  border: 1.5px solid #fe672c;
}
.ph-success-stage--error h1 {
  color: #c0392b;
}
</style>
